import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Radio, Tooltip, Typography } from '@navi/web-ui/lib/primitives';
import styles from './Enach.module.scss';
import { DownloadIcon, InfoIcon } from '@navi/web-ui/lib/icons';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { ENACH_CLICKSTREAM } from '@cp/src/service/clickStream.constant';
import { useColumnDefs } from './ColumnDefs';
import useUpload from '@cp/src/components/UploadComponent/useUpload';
import axiosInstance, { ApiKeys, getApiUrl } from '@cp/src/utils/ApiHelper';
import { downloadContent } from '@cp/src/utils/commonUtils';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { fetchUploadHistory } from './EnachUploadHistory/enachUploadHistoyDrawerActions';
import {
  ENACH_TRIGGER_TYPE,
  FILE_STATUS,
  withAmountCsvFile,
  withoutAmountCsvFile
} from './Constants/types';
import CustomHistoryIcon from '@cp/src/assets/icons/CustomHistoryIcon';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import UploadHistoryComponent from './Components/EnachUploadHistoryDrawer';
import FileUploadStateComponent from './Components/FileUploadStateComponent';
import { GenericObject } from '@cp/src/types/CommonConstans';

const CashCollectedCard = () => {
  const featureFlags = useSelector((state: RootState) => state?.common?.featureFlags);
  const [showUploadHistory, setShowUploadHistory] = useState(false);
  const { columnDefs: columnDefsUploadHistory } = useColumnDefs();
  const [enachTriggerType, setEnachTriggerType] = useState<ENACH_TRIGGER_TYPE | null>(null);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
  const [referenceId, setReferenceId] = useState(null);
  const [showLoadingComponent, setShowLoadingComponent] = useState(false);

  const handleRadioChange = (value: ENACH_TRIGGER_TYPE) => {
    setEnachTriggerType(value);
    addClickstreamEvent(ENACH_CLICKSTREAM.LH_ENACH_TRIGGER_TYPE_SELECTED, {
      enachType: value
    });
  };
  const handleUploadHistory = () => {
    addClickstreamEvent(ENACH_CLICKSTREAM.LH_VIEW_ENACH_UPLOAD_HISTORY_CLICKED);
    setShowUploadHistory(true);
  };

  const historyData = useSelector((store: RootState) => store?.enachSlice?.data);
  const { enachPartialProcessingEnabled, enachForeclosureProcessingEnabled } = featureFlags || {};

  const transformRowData = () => {
    return historyData;
  };

  const onFileUploadedError = () => {
    setIsSubmitButtonDisabled(true);
    setError({
      message: 'An error occurred while uploading the file',
      fileName: file?.name || ''
    });
    toast.error('Failed to upload the File');
  };
  const onFileUploadedSuccess = async (res?: GenericObject) => {
    const ackUrl = getApiUrl(ApiKeys.ENACH_UPLOAD_ACK_URL);
    try {
      const response = await axiosInstance.post(ackUrl, {
        referenceId: res?.data?.referenceId,
        status: 'UPLOAD_SUCCESS',
        message: '',
        metadata: {
          uploadedFileName: file?.name,
          documentType: enachTriggerType
        }
      });

      if (response?.data?.status === FILE_STATUS.FAILED) {
        addClickstreamEvent(ENACH_CLICKSTREAM.LH_ENACH_TRIGGER_FILE_UPLOAD_FAILED, {
          enachType: enachTriggerType,
          reason: 'File upload failed. Please verify the data entries'
        });
        const errorMessage =
          response?.data?.metadata?.errorMessage ||
          'File upload failed. Please verify the data entries';
        setIsSubmitButtonDisabled(true);
        setError({
          message: errorMessage,
          fileName: file?.name || ''
        });
        toast.error(errorMessage);
        setShowLoadingComponent(false);
      } else {
        setError({ message: '', fileName: '' });
        setIsSubmitButtonDisabled(false);
        setShowLoadingComponent(false);
        addClickstreamEvent(ENACH_CLICKSTREAM.LH_ENACH_TRIGGER_FILE_UPLOAD_SUCCESSFUL, {
          enachType: enachTriggerType
        });
      }
    } catch (err) {
      addClickstreamEvent(ENACH_CLICKSTREAM.LH_ENACH_TRIGGER_FILE_UPLOAD_FAILED, {
        enachType: enachTriggerType,
        reason: 'An error occurred while uploading the file'
      });
      setError({
        message: 'An error occurred while uploading the file.',
        fileName: file?.name || ''
      });
      toast.error('Failed to upload the File');
    } finally {
      setShowLoadingComponent(false);
    }

    setReferenceId(res?.data?.referenceId);
  };

  const { file, setFile, upload, error, setError } = useUpload({
    onFileUploadedSuccess: onFileUploadedSuccess,
    onFileUploadedError: onFileUploadedError
  });

  useEffect(() => {
    if (file && enachTriggerType) {
      setShowLoadingComponent(true);
      const getUploadUrl = getApiUrl(
        ApiKeys.GET_ENACH_UPLOAD_URL,
        {},
        {
          documentType: enachTriggerType,
          documentContentType: 'CSV'
        }
      );
      axiosInstance.get(getUploadUrl).then(async res => {
        setIsSubmitButtonDisabled(true);
        try {
          await upload(file, res?.data?.preSignedPutUrl, res);
        } catch {
          setError({
            message: 'File upload failed. Please verify the data entries',
            fileName: file?.name || ''
          });
        } finally {
          setIsSubmitButtonDisabled(false);
        }
      });
    } else {
      setIsSubmitButtonDisabled(true);
    }
  }, [file, enachTriggerType]);

  const handleSubmitClick = useCallback(
    async (referenceId: string) => {
      addClickstreamEvent(ENACH_CLICKSTREAM.LH_ENACH_TRIGGER_FILE_SUBMITTED, {
        enachType: enachTriggerType
      });
      try {
        const apiUrl = getApiUrl(ApiKeys.UPLOAD_ENACH_DOCUMENT, { referenceId });
        await axiosInstance.patch(apiUrl).then(response => {
          if (response?.data?.status === FILE_STATUS.COMPLETED) {
            toast.success('File successfully uploaded');
          } else if (response?.data?.status === FILE_STATUS.PENDING) {
            toast.info(
              <div className="text-sm">
                File upload in progress... Please check the <br /> Upload History for status.
              </div>
            );
          }
        });
        addClickstreamEvent(ENACH_CLICKSTREAM.LH_ENACH_FILE_SUBMISSION_SUCCESSFUL, {
          enachType: enachTriggerType
        });
        setReferenceId(null);
        setFile(null);
        setEnachTriggerType(null);
        setIsSubmitButtonDisabled(true);
      } catch (error) {
        addClickstreamEvent(ENACH_CLICKSTREAM.LH_ENACH_FILE_SUBMISSION_FAILED, {
          enachType: enachTriggerType,
          reason: 'File upload failed due to validation failure'
        });
        toast.error('Failed to upload the File');
      }
    },
    [referenceId]
  );

  const handleClick = () => {
    if (referenceId !== null) {
      handleSubmitClick(referenceId);
    }
  };

  const handleDownloadSampleFileClick = (
    enachType: string,
    content: string[],
    name: string,
    type: string
  ) => {
    addClickstreamEvent(ENACH_CLICKSTREAM.LH_DOWNLOAD_SAMPLE_FILE_ENACH, {
      enachType: enachType
    });
    downloadContent(content, name, type);
  };
  return (
    <div className="w-full h-full flex justify-center items-center">
      <div className={styles.parentContainer}>
        <div className={styles.viewHistory}>
          <Button
            variant="text"
            size="medium"
            startAdornment={
              <CustomHistoryIcon fillColor="var(--blue-base)" width={20} height={20} />
            }
            onClick={handleUploadHistory}
          >
            View Upload History
            <UploadHistoryComponent
              showUploadHistoryComponent={showUploadHistory}
              setShowUploadHistoryComponent={setShowUploadHistory}
              data={historyData}
              transformRowData={transformRowData}
              columnDefs={columnDefsUploadHistory}
              fetchUploadHistoryPolling={fetchUploadHistory}
              headerText="E-NACH History"
              typeOfUpload={
                'OVERDUE_ENACH_PRESENTATION,CUSTOM_ENACH_PRESENTATION,FORECLOSURE_ENACH_PRESENTATION'
              }
              width={'720px'}
              status={'APPLIED,APPLY_INPROGRESS,APPLY_FAILED,VALIDATION_FAILURE'}
            />
          </Button>
        </div>
        <Card className={styles.cardLayout}>
          <Typography variant="h3" className={styles.header}>
            Choose ENACH trigger type & Select the File
          </Typography>
          <div className={styles.radioButtons}>
            {enachPartialProcessingEnabled ? (
              <>
                <div className={styles.radio}>
                  <div className={styles.selectTriggerType}>
                    <Radio
                      id="custom"
                      name="enachTriggerType"
                      value="CUSTOM"
                      variant="sm"
                      checked={enachTriggerType === ENACH_TRIGGER_TYPE.CUSTOM}
                      disabled={
                        showLoadingComponent && enachTriggerType !== ENACH_TRIGGER_TYPE.CUSTOM
                      }
                      label="With Specified Amount"
                      onClick={() => handleRadioChange(ENACH_TRIGGER_TYPE.CUSTOM)}
                    />
                    <div className={styles.info}>
                      <Tooltip position="top" text="File contains LANs and amount" withPointer>
                        <Typography variant="h4">
                          <InfoIcon color="var(--grayscale-2)" width={16} height={16} />
                        </Typography>
                      </Tooltip>
                    </div>
                  </div>
                  <div className={styles.selectFileType}>
                    <Button
                      variant="text"
                      onClick={() =>
                        handleDownloadSampleFileClick(
                          ENACH_TRIGGER_TYPE.CUSTOM,
                          [withAmountCsvFile],
                          'sample_specified_amount.csv',
                          'csv'
                        )
                      }
                      className={styles.selectFileType}
                      startAdornment={
                        <DownloadIcon color="var(--blue-base)" className={styles.downloadIcon} />
                      }
                    >
                      <Typography variant="p1" className={styles.sampleText}>
                        Sample
                      </Typography>
                    </Button>
                  </div>
                </div>
                <div className={styles.radio}>
                  <div className={styles.selectTriggerType}>
                    <Radio
                      id="overdue"
                      name="enachTriggerType"
                      value="OVERDUE"
                      disabled={
                        showLoadingComponent && enachTriggerType !== ENACH_TRIGGER_TYPE.OVERDUE
                      }
                      checked={enachTriggerType === ENACH_TRIGGER_TYPE.OVERDUE}
                      variant="sm"
                      label="With Total Overdue"
                      onClick={() => handleRadioChange(ENACH_TRIGGER_TYPE.OVERDUE)}
                    />
                    <div className={styles.info}>
                      <Tooltip position="top" text="File contains only LANs" withPointer>
                        <Typography variant="h4">
                          <InfoIcon color="var(--grayscale-2)" width={16} height={16} />
                        </Typography>
                      </Tooltip>
                    </div>
                  </div>
                  <div className={styles.selectFileType}>
                    <Button
                      variant="text"
                      onClick={() =>
                        handleDownloadSampleFileClick(
                          ENACH_TRIGGER_TYPE.OVERDUE,
                          [withoutAmountCsvFile],
                          'sample_total_overdue.csv',
                          'csv'
                        )
                      }
                      className={styles.selectFileType}
                      startAdornment={
                        <DownloadIcon color="var(--blue-base)" className={styles.downloadIcon} />
                      }
                    >
                      <Typography variant="p1" className={styles.sampleText}>
                        Sample
                      </Typography>
                    </Button>
                  </div>
                </div>
              </>
            ) : null}

            {enachForeclosureProcessingEnabled ? (
              <div className={styles.radio}>
                <div className={styles.selectTriggerType}>
                  <Radio
                    id="overdue"
                    name="enachTriggerType"
                    value="FORECLOSURE"
                    disabled={
                      showLoadingComponent && enachTriggerType !== ENACH_TRIGGER_TYPE.FORECLOSURE
                    }
                    checked={enachTriggerType === ENACH_TRIGGER_TYPE.FORECLOSURE}
                    variant="sm"
                    label="With Foreclosure Amount"
                    onClick={() => handleRadioChange(ENACH_TRIGGER_TYPE.FORECLOSURE)}
                  />
                  <div className={styles.info}>
                    <Tooltip position="top" text="File contains only LANs" withPointer>
                      <Typography variant="h4">
                        <InfoIcon color="var(--grayscale-2)" width={16} height={16} />
                      </Typography>
                    </Tooltip>
                  </div>
                </div>
                <div className={styles.selectFileType}>
                  <Button
                    variant="text"
                    onClick={() =>
                      handleDownloadSampleFileClick(
                        ENACH_TRIGGER_TYPE.FORECLOSURE,
                        [withoutAmountCsvFile],
                        'sample_foreclosure_amount.csv',
                        'csv'
                      )
                    }
                    className={styles.selectFileType}
                    startAdornment={
                      <DownloadIcon color="var(--blue-base)" className={styles.downloadIcon} />
                    }
                  >
                    <Typography variant="p1" className={styles.sampleText}>
                      Sample
                    </Typography>
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
          <FileUploadStateComponent
            error={error}
            file={file}
            setFile={setFile}
            setError={setError}
            enachTriggerType={enachTriggerType}
            showLoadingComponent={showLoadingComponent}
          />
          <div className={styles.submitOption}>
            <Button
              type="submit"
              className={styles.submitButton}
              disabled={isSubmitButtonDisabled}
              onClick={() => handleClick()}
            >
              Submit
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CashCollectedCard;
